import { FC, useCallback } from 'react';
import { trackEvent } from 'dibs-tracking';
import { useFragment, graphql } from 'react-relay';
import { fireReadMoreTracking } from '../../shared/PdpSharedStoryTelling/pdpSharedStoryTellingHelpers';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea';

// styles
import styles from './PdpRespSharedStoryTellingText.scss';

// types
import { PdpRespSharedStoryTellingText_storyTellingTab$key as StoryTellingTab } from './__generated__/PdpRespSharedStoryTellingText_storyTellingTab.graphql';

const storyTellingTabQuery = graphql`
    fragment PdpRespSharedStoryTellingText_storyTellingTab on StoryTellingTab {
        tabType
        tabTitle
        contentHeader
        contentCreatorAttribution {
            creatorName
            creatorBrowseUrl {
                ...SeoLink_linkData
            }
        }
        contentBodyText
    }
`;
export const PdpRespSharedStoryTellingText: FC<{
    showHeader?: boolean;
    showExpandingArea?: boolean;
    storyTellingTab: StoryTellingTab;
}> = ({ storyTellingTab: storyTellingTabRef, showExpandingArea = true, showHeader = true }) => {
    const storyTellingTab = useFragment(storyTellingTabQuery, storyTellingTabRef);
    const { tabTitle, contentHeader, contentCreatorAttribution, contentBodyText, tabType } =
        storyTellingTab;

    const onReadMoreClick = useCallback(() => {
        fireReadMoreTracking({ tabType });
    }, [tabType]);

    const content = (
        <div
            className={styles.contentBodyText}
            dangerouslySetInnerHTML={{ __html: contentBodyText }}
        />
    );

    return (
        <HeadingLevel>
            {Heading => (
                <>
                    {showHeader && (
                        <Heading dataTn="pdp-storytelling-about-info" className={styles.tabTitle}>
                            {tabTitle}
                        </Heading>
                    )}
                    {contentHeader && (
                        <div className={styles.contentHeaderWrapper}>
                            <Heading
                                dataTn="pdp-storytelling-creator-name"
                                className={styles.contentHeader}
                                dangerouslySetInnerHTML={{ __html: contentHeader }}
                            />
                        </div>
                    )}

                    {contentCreatorAttribution && (
                        <div className={styles.creatorAttribution}>
                            <FormattedMessage
                                defaultMessage="Designed by {creator}"
                                id="PdpRespSharedStorytellingText.creatorAttribution"
                                values={{
                                    creator: (
                                        <SeoLink
                                            linkData={contentCreatorAttribution.creatorBrowseUrl}
                                            dataTn="pdp-storytelling-design-attribution-link"
                                            onClick={() => {
                                                trackEvent({
                                                    label: 'pdp',
                                                    category: 'promo interaction',
                                                    action: 'iconic design creator clicked',
                                                });
                                            }}
                                        >
                                            {contentCreatorAttribution?.creatorName}
                                        </SeoLink>
                                    ),
                                }}
                            />
                        </div>
                    )}
                    {showExpandingArea ? (
                        <ExpandingAreaWithReadMore
                            showGradient={false}
                            collapsedHeight={120}
                            onReadMoreClick={onReadMoreClick}
                            wrapperClass={styles.expandingAreaWrapper}
                            readMore={
                                <FormattedMessage
                                    defaultMessage="Read more"
                                    id="PdpRespSharedStorytellingText.readMoreLink"
                                />
                            }
                        >
                            {content}
                        </ExpandingAreaWithReadMore>
                    ) : (
                        content
                    )}
                </>
            )}
        </HeadingLevel>
    );
};
